import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { LogoMark } from "./components/Logo";
import {
  VSLPlaceholder,
  StickyCTABar,
  FeaturedInStrip,
  GuaranteeBadge,
} from "./components/ConversionKit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#3B5EDF";
const BLUE = "#3B5EDF";
const LIME = "#D9F154";
const CREAM = "#141414";
const BG = "#FAF7EF";
const BG_ALT = "#FFFFFF";
const BG_ELEV = "#FFFFFF";

const VIDEOS = [
  { id: "L4ILB77pxUo", title: "Entrevista a Ernesto Gómez · Finmex", channel: "FranchiseZar · Entrevista", thumb: "/forbes-ernesto-finmex.jpg" },
  { id: "7SAmE2wMLb4", title: "La Evolución de la banca en México", channel: "Foro Forbes · 2015" },
  { id: "TqlbfZ1m7gQ", title: "Finmex: 8 años con los emprendedores", channel: "Foro Forbes Mujeres Poderosas" },
].map((v) => ({ ...v, url: `https://www.youtube.com/watch?v=${v.id}`, thumb: v.thumb || `https://img.youtube.com/vi/${v.id}/hqdefault.jpg` }));

const T = {
  es: {
    promo: "The Debt Guy · Ernesto Gómez Berjón",
    nav: { about: "sobre mí", teardown: "teardown", programs: "programas", back: "← tracks" },
    hero: {
      badge: "USD 1.2B+ EN TERM SHEETS · MANAGING PARTNER ERGO CAPITAL · MENTOR ENDEAVOR & YC",
      title1: "Deuda institucional,",
      title2: "explicada por quien",
      titleHL: "la cierra.",
      sub: "Estructuro capital para fintechs, SOFOMs, fondos y empresas en LATAM desde hace más de una década. Aquí abro mi método: operaciones reales de capital, por dentro. Y de vez en cuando te enseño cómo levantar el tuyo.",
      cta: "Quiero el método gratis",
      ctaSub: "Material gratis directo a tu correo · sin spam",
      diag: "Agenda un diagnóstico",
    },
    proof: {
      tag: "— TRACK RECORD",
      stats: [
        { n: "$3B+", l: "pipeline de deals originados" },
        { n: "$1.2B+", l: "en term sheets firmados" },
        { n: "16+", l: "años en la industria" },
        { n: "9", l: "jurisdicciones · LATAM · US · EU" },
      ],
      logos: ["Kapital Bank", "Atrato", "Trafalgar", "EDYEN", "BAASA", "Grupo Fagro", "Cobre", "MelonCash", "Quipu Bank", "Credilikeme", "ConFeria", "ed.fi"],
      fundsTag: "— FONDOS Y BANCOS CON LOS QUE HE ESTRUCTURADO",
      funds: ["SixPoint Capital", "Accial Capital", "Sivo", "Kapital Bank", "B×+", "INVEX", "Multiva", "Pictet"],
      credsTag: "— CREDENCIALES",
      creds: ["CFA", "FRM", "CAIA", "ASA", "CIRA", "CRC", "AIF®", "FINRA Series 79", "FINRA Series 65", "MexDer Series 315", "AMIB"],
    },
    story: {
      tag: "— LA HISTORIA",
      hook: "Lo que aprendí cerrando deals que los bancos rechazaban.",
      title1: "Pasé 16 años haciendo",
      title2: "lo que nadie te enseña",
      titleHL: "en la escuela.",
      body: [
        "Empecé armando estructuras para fintechs que nadie más entendía. Cross-border. Intangibles como colateral. ISDAs bespoke para SOFOMs reguladas. Lo difícil.",
        'Con el tiempo me volví el tipo al que le hablaban cuando los bancos decían no — "háblale a Ernesto". Fundé ERGO Capital y Sivo. Estructuré deals para Kapital, Atrato, Trafalgar, EDYEN, BAASA, Grupo Fagro, Cobre, Quipu, Credilikeme y más. Originé y negocié líneas con SixPoint, Accial, B×+, INVEX, Multiva y Pictet en Suiza.',
        "En el camino me volví mentor de Endeavor y de compañías de Y Combinator, y consejero de fintechs, fondos y PyMEs. Sumé las credenciales que respaldan el método — CFA, FRM, CAIA, ASA, CIRA, CRC, AIF® — más las licencias FINRA (Series 79 y 65), MexDer 315 y AMIB.",
        "Pero lo que más noté fue esto: el 90% de los founders y operadores con los que hablaba no tenían idea de cómo pensaba el otro lado. Traían deck, pero no estructura. Traían producto, pero no deal.",
        "The Debt Guy nació de ahí: de abrir el método que uso cada semana, a un grupo de gente que está construyendo lo suyo y quiere dejar de perder meses — y millones en dilución — por no saber cómo estructurar.",
      ],
      role: "Managing Partner · ERGO Capital · CDMX",
    },
    forWho: {
      tag: "— PARA QUIÉN ES",
      title1: "Si eres",
      titleHL: "operador,",
      title2: "esto es para ti.",
      cards: [
        { k: "LENDERS / INSTITUCIONES", d: "Founder o CFO de fintech, SOFOM o fondo de private credit. Cartera $10-500M. Buscas tu primera facilidad institucional." },
        { k: "PYMES", d: "Dueño o CFO de empresa con facturación real. Buscas capital de trabajo o crédito bancario sin regalar equity." },
        { k: "PROFESIONALES", d: "Analista, banquero, asesor. Quieres entrar a DCM o private credit. Quieres hablar el idioma del capital." },
      ],
    },
    products: {
      tag: "— LO QUE OFREZCO",
      hook: "Un funnel completo: gratis, ebooks, cursos, certificación, membresía y cohortes.",
      title1: "Seis escalones.",
      titleHL: "Un método.",
      ladder: [
        { tier: "GRATIS", n: "Ebooks gratis", d: "Descarga el método por vertical, en PDF, a cambio de tu correo.", price: "$0", cta: "Descargar gratis", to: "/#biblioteca", flag: "empieza aquí" },
        { tier: "ENTRADA", n: "The Debt Toolkit", d: "Modelos, checklists y plantillas para preparar tu fondeo.", price: "$149", cta: "Ver Toolkit", to: "/toolkit", flag: "self-serve" },
        { tier: "CURSOS", n: "Programas por vertical", d: "Ebook + curso de PyMEs, Lenders / Instituciones o FinCorp. Compra directa.", price: "desde $19", cta: "Ver programas", to: "/programas", flag: "3 verticales" },
        { tier: "CERT", n: "Certificación FinCorp", d: "Corporate Finance & Capital Markets, avalada por IADA Anáhuac.", price: "$2,500", cta: "Ver certificación", to: "/certificacion", flag: "avalada Anáhuac" },
        { tier: "MRR", n: "The Debt Circle", d: "Membresía: plantillas, office hours, comunidad y deal-flow.", price: "$79/mo", cta: "Ver Circle", to: "/circle", flag: "recurrente" },
        { tier: "COHORTE", n: "The Debt Program", d: "8–10 semanas acompañado. Termina con term sheet negociable.", price: "inscripción directa", cta: "Ver e inscribirme", to: "/programas", flag: "premium" },
      ],
    },
    cta: {
      tag: "— EMPIEZA AQUÍ",
      title1: "Empieza gratis.",
      title2: "Descarga tu",
      titleHL: "ebook.",
      desc: "El método, en PDF. Sin fluff. Sin motivación. Solo mecánica.",
      primary: "Quiero el método gratis",
      secondary: "Agenda diagnóstico 20-min",
    },
    footer: "the debt guy · © 2026",
    langBtn: "EN",
  },

  en: {
    promo: "The Debt Guy · Ernesto Gómez Berjón",
    nav: { about: "about", teardown: "teardown", programs: "programs", back: "← tracks" },
    hero: {
      badge: "USD 1.2B+ IN SIGNED TERM SHEETS · MANAGING PARTNER · ERGO CAPITAL",
      title1: "Institutional debt,",
      title2: "explained by the",
      titleHL: "one who closes it.",
      sub: "I've structured capital for fintechs, non-bank lenders, funds and companies across LATAM for over a decade. In The Teardown I break down real capital deals, up close. Every now and then I teach you how to raise yours.",
      cta: "Subscribe to the Teardown",
      ctaSub: "Weekly newsletter · free · +250 new subscribers/week",
      diag: "Book a diagnostic",
    },
    proof: {
      tag: "— PROOF",
      stats: [
        { n: "$1.2B+", l: "in signed term sheets" },
        { n: "10+", l: "years structuring" },
        { n: "9", l: "jurisdictions · LATAM · US · EU" },
        { n: "47", l: "deals closed" },
      ],
      logos: ["ERGO Capital", "Sivo", "Trafalgar", "Grupo Fagro", "Kapital México", "BAASA"],
    },
    story: {
      tag: "— THE STORY",
      hook: "What I learned closing the deals banks refused.",
      title1: "I spent 10 years doing",
      title2: "what nobody teaches you",
      titleHL: "in school.",
      body: [
        "I started building structures for fintechs no one else understood. Cross-border. Intangibles as collateral. Bespoke ISDAs for regulated non-bank lenders. The hard stuff.",
        'Over time I became the guy they called when the banks said no — "call Ernesto". I founded ERGO Capital. Structured deals for Trafalgar, Grupo Fagro, Kapital México, BAASA. Built vehicles in Delaware and Mexico. Raised rounds, refinanced nine-figure portfolios, negotiated term sheets against funds and commercial banks.',
        "But what I noticed most was this: 90% of the founders and operators I talked to had no idea how the other side thinks. They had a deck, but no structure. They had product, but no deal.",
        "The Debt Guy was born from that: from opening up the method I use every week, to a group of people building their own thing who want to stop losing months — and millions in dilution — for not knowing how to structure.",
      ],
      role: "Managing Partner · ERGO Capital · Mexico City",
    },
    forWho: {
      tag: "— WHO IT'S FOR",
      title1: "If you're an",
      titleHL: "operator,",
      title2: "this is for you.",
      cards: [
        { k: "LENDERS / INSTITUTIONS", d: "Fintech or non-bank lender founder / CFO. Portfolio $10-500M. Looking for your first institutional facility." },
        { k: "SMBS", d: "Owner or CFO of a real-revenue business. Looking for working capital or bank credit without giving up equity." },
        { k: "PROFESSIONALS", d: "Analyst, banker, advisor. Want to move into DCM or private credit. Want to speak the language of capital." },
      ],
    },
    products: {
      tag: "— WHAT I OFFER",
      hook: "A complete funnel: free, entry, course, cohort, private circle.",
      title1: "5 rungs.",
      titleHL: "One method.",
      ladder: [
        { tier: "FREE", n: "The Teardown", d: "A real term sheet, torn apart. Every Thursday.", price: "$0", cta: "Subscribe", to: "/teardown", flag: "start here" },
        { tier: "MRR", n: "Teardown Pro", d: "Full archive + models + private Slack + 2 live teardowns/mo.", price: "$29/mo", cta: "See Pro", to: "/teardown", flag: "membership" },
        { tier: "MRR", n: "The Capital Academy", d: "Debt Fundamentals + Lender 101 full courses + 60+ template library.", price: "$49/mo", cta: "See Academy", to: "/teardown", flag: "coming soon" },
        { tier: "MRR", n: "Debt Circle", d: "Private membership: monthly office hours, deal directory, curated network of 80+ counterparties.", price: "$499/mo", cta: "See Circle", to: "/teardown", flag: "invite only" },
        { tier: "COHORT", n: "The Debt Program", d: "8 weeks, 10 lenders, 2 cohorts/year. Ends with Demo Day and a term sheet.", price: "$7.5k", cta: "Apply", to: "/programas", flag: "for lenders" },
      ],
    },
    cta: {
      tag: "— START HERE",
      title1: "Start free.",
      title2: "Subscribe to the",
      titleHL: "Teardown.",
      desc: "Real capital deals torn apart. No fluff. No motivation. Just mechanics.",
      primary: "Subscribe to the Teardown",
      secondary: "Book a 20-min diagnostic",
    },
    footer: "the debt guy · © 2026",
    langBtn: "ES",
  },
};

export default function Ernesto() {
  const [lang] = useState("es");
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "The Debt Guy · Ernesto Gómez Berjón";
  }, []);

  const t = T[lang];

  const subscribe = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email || !email.includes("@")) {
      setErr(lang === "es" ? "Escribe un email válido." : "Enter a valid email.");
      return;
    }
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, source: "ernesto_page", lang });
      setSubscribed(true);
    } catch {
      setErr(lang === "es" ? "Algo salió mal. Intenta de nuevo." : "Something went wrong. Try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div style={{ background: BG, color: CREAM, minHeight: "100vh", overflowX: "hidden" }} data-testid="ernesto-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100..900;1,100..900&family=Archivo+Black&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { overflow-x: hidden; width: 100%; background: ${BG}; }
        body { font-family: 'Archivo', sans-serif; color: ${CREAM}; -webkit-font-smoothing: antialiased; }
        h1, h2, h3, .hero-title, .section-title, .hub-hero-title, .fraunces-hero, .track-title, .track-subtitle, .pro-price { font-family: 'Archivo', sans-serif !important; font-style: normal !important; letter-spacing: -0.03em; }
        h1, h2, .hero-title, .section-title, .hub-hero-title, .fraunces-hero, .track-subtitle, .pro-price { font-weight: 900 !important; }
        .track-title { font-weight: 800 !important; }
        h1 .serif, h2 .serif, .hero-title .serif, .section-title .serif, .hub-hero-title .serif { font-weight: 900 !important; font-style: normal !important; }
        .tag { font-weight: 700 !important; }
        ::selection { background: ${LIME}; color: #141414; }

        .grain::before {
          content: ''; position: fixed; inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E");
          pointer-events: none; z-index: 1; mix-blend-mode: overlay;
        }
        .serif { font-family: 'Archivo', sans-serif; font-weight: 800; letter-spacing: -0.02em; }
        .mono { font-family: 'Archivo', sans-serif; }
        .tag { font-family: 'Archivo', sans-serif; font-size: 10.5px; letter-spacing: 0.3em; text-transform: uppercase; color: ${GOLD}; }

        .hero-title { font-size: clamp(48px, 8vw, 128px); font-weight: 900; line-height: 0.94; letter-spacing: -0.045em; }
        .section-title { font-size: clamp(36px, 5vw, 76px); font-weight: 900; line-height: 1.02; letter-spacing: -0.035em; }

        .btn-primary {
          background: ${BLUE}; color: #FFFFFF; padding: 16px 32px;
          font-family: 'Archivo', sans-serif; font-size: 11px; font-weight: 500;
          letter-spacing: 0.06em; text-transform: uppercase; border: 2px solid #141414; border-radius: 14px; cursor: pointer; box-shadow: 4px 4px 0 #141414; font-weight: 800;
          transition: all 0.3s; display: inline-block; text-decoration: none; text-align: center;
        }
        .btn-primary:hover { transform: translate(2px,2px); box-shadow: 2px 2px 0 #141414; }
        .btn-ghost {
          background: transparent; color: ${CREAM}; padding: 16px 32px;
          font-family: 'Archivo', sans-serif; font-size: 11px; font-weight: 500;
          letter-spacing: 0.2em; text-transform: uppercase;
          border: 1px solid rgba(245, 240, 230, 0.25); cursor: pointer;
          transition: all 0.3s; display: inline-block; text-decoration: none; text-align: center;
        }
        .btn-ghost:hover { border-color: ${GOLD}; color: ${GOLD}; }

        .promo-bar {
          background: ${BLUE}; color: #FFFFFF; padding: 9px 20px; text-align: center;
          font-family: 'Archivo', sans-serif; font-size: 10.5px; letter-spacing: 0.15em;
          text-transform: uppercase; font-weight: 500;
        }

        .lang-btn {
          font-family: 'Archivo', sans-serif; font-size: 10.5px;
          letter-spacing: 0.2em; text-transform: uppercase;
          padding: 8px 14px; border: 1px solid rgba(15,56,44,0.15);
          background: transparent; color: ${CREAM}; cursor: pointer;
          transition: all 0.25s;
        }
        .lang-btn:hover { border-color: ${GOLD}; color: ${GOLD}; }

        .portrait {
          width: 100%; aspect-ratio: 4/5; background: ${BG_ALT};
          background-image: linear-gradient(135deg, rgba(15,56,44,0.08), transparent 60%), url('https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?q=80&w=800&auto=format&fit=crop');
          background-size: cover; background-position: center 20%;
          border: 1px solid rgba(15,56,44,0.15);
          filter: grayscale(20%) contrast(1.05);
          position: relative;
        }
        .portrait::after {
          content: ""; position: absolute; inset: 0;
          background: linear-gradient(180deg, transparent 40%, ${BG}88 100%);
          pointer-events: none;
        }

        .grid-hero { display: grid; grid-template-columns: 1.35fr 1fr; gap: 64px; align-items: center; }
        .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2px; background: ${CREAM}11; border: 1px solid ${CREAM}11; }
        .card { background: ${BG_ALT}; padding: 36px 30px; transition: background 0.3s; }
        .card:hover { background: ${BG_ELEV}; }

        .ladder-row {
          background: ${BG_ALT}; padding: 26px 28px;
          display: grid; grid-template-columns: 90px 1.4fr 2fr 90px auto;
          gap: 28px; align-items: center;
          border: 1px solid rgba(15,56,44,0.05);
          transition: all 0.3s;
        }
        .ladder-row:hover { border-color: ${GOLD}44; background: ${BG_ELEV}; transform: translateX(4px); }
        .ladder-tier {
          font-family: 'Archivo', sans-serif; font-size: 9px;
          letter-spacing: 0.28em; text-transform: uppercase;
          color: ${GOLD}; padding: 4px 8px; border: 1px solid ${GOLD}55;
          width: fit-content;
        }

        .newsletter-form {
          display: flex; gap: 10px; margin-top: 24px; max-width: 460px; flex-wrap: wrap;
        }
        .newsletter-input {
          flex: 1; min-width: 200px;
          background: #FFFFFF; border: 2px solid #141414; border-radius: 10px;
          color: ${CREAM}; font-family: 'Archivo', sans-serif; font-size: 14px;
          padding: 15px 16px; outline: none; border-radius: 10px;
        }
        .newsletter-input:focus { border-color: ${GOLD}; }

        .logo-strip {
          display: flex; flex-wrap: wrap; gap: 32px 40px;
          margin-top: 32px; opacity: 0.7;
          font-family: 'Archivo', sans-serif; font-size: 11px;
          letter-spacing: 0.24em; text-transform: uppercase; color: ${CREAM};
        }

        @media (max-width: 900px) {
          .grid-hero { grid-template-columns: 1fr !important; gap: 32px !important; }
          .grid-3 { grid-template-columns: 1fr !important; }
          .ladder-row { grid-template-columns: 1fr !important; gap: 14px !important; }
          [data-testid="ernesto-nav"] { padding: 14px 18px !important; }
          [data-testid="ernesto-nav"] > div:last-child { gap: 10px !important; }
        }
        @media (max-width: 720px) {
          [data-testid="ernesto-nav"] [data-testid="ernesto-nav-teardown"],
          [data-testid="ernesto-nav"] [data-testid="ernesto-nav-back"] { display: none !important; }
        }
        @media (max-width: 480px) {
          [data-testid="ernesto-nav"] .brand-sub { display: none !important; }
          [data-testid="ernesto-nav-cta"] { padding: 10px 14px !important; font-size: 9px !important; }
        }
        .brand-text-wrap { white-space: nowrap; }
      `}</style>

      <div className="grain" />

      <div className="promo-bar" data-testid="ernesto-promo">{t.promo}</div>

      <nav
        style={{
          position: "sticky", top: 0, zIndex: 30,
          background: `${BG}dd`, backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(15,56,44,0.06)",
          padding: "18px 40px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}
        data-testid="ernesto-nav"
      >
        <Link to="/ernesto" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: CREAM }}>
          <LogoMark size={62} />
        </Link>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <Link to="/" className="mono" style={{ fontSize: 10.5, letterSpacing: "0.2em", textTransform: "uppercase", color: CREAM, opacity: 0.55, textDecoration: "none" }} data-testid="ernesto-nav-back">
            {t.nav.back}
          </Link>
          <Link to="/programas" className="btn-primary" data-testid="ernesto-nav-cta" style={{ padding: "12px 22px", fontSize: 10 }}>
            {lang === "es" ? "ver precios" : "see pricing"}
          </Link>
        </div>
      </nav>

      {/* HERO */}
      <section
        style={{ padding: "120px 40px 90px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 5 }}
        data-testid="ernesto-hero"
      >
        <div className="grid-hero">
          <div>
            <div className="tag" data-testid="ernesto-hero-badge">
              <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: GOLD, marginRight: 10, verticalAlign: "middle" }} />
              {t.hero.badge}
            </div>

            <h1 className="hero-title" style={{ marginTop: 30 }} data-testid="ernesto-hero-title">
              {t.hero.title1}<br />
              {t.hero.title2}<br />
              <span className="serif" style={{ fontStyle: "normal", color: GOLD, fontWeight: 400 }}>{t.hero.titleHL}</span>
            </h1>

            <p className="serif" style={{ marginTop: 30, fontSize: "clamp(17px, 1.55vw, 21px)", lineHeight: 1.5, maxWidth: 620, opacity: 0.82, fontStyle: "normal" }} data-testid="ernesto-hero-sub">
              {t.hero.sub}
            </p>

            <form id="newsletter" className="newsletter-form" onSubmit={subscribe} data-testid="ernesto-newsletter-form">
              <input
                className="newsletter-input"
                type="email"
                required
                placeholder={lang === "es" ? "tu@email.com" : "you@email.com"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={subscribing || subscribed}
                data-testid="ernesto-newsletter-input"
              />
              <button type="submit" className="btn-primary" disabled={subscribing || subscribed} data-testid="ernesto-newsletter-submit">
                {subscribed ? (lang === "es" ? "¡listo ✓!" : "done ✓") : subscribing ? "..." : t.hero.cta}
              </button>
            </form>
            <div className="mono" style={{ marginTop: 12, fontSize: 10, letterSpacing: "0.16em", opacity: 0.55, textTransform: "uppercase" }} data-testid="ernesto-newsletter-sub">
              {t.hero.ctaSub}
            </div>
            {err && <div style={{ color: "#F97316", fontSize: 13, marginTop: 10 }} data-testid="ernesto-newsletter-err">{err}</div>}
          </div>

          <div style={{ position: "relative" }}>
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_raise-like-insider/artifacts/49gzz0ou_31066C61-A699-4CE6-96B2-1FEA5AB108AD.png"
              alt="Ernesto Gómez Berjón"
              data-testid="ernesto-portrait"
              style={{ width: "100%", maxWidth: 460, borderRadius: 18, border: "2px solid #141414", boxShadow: "8px 8px 0 #141414", display: "block", objectFit: "cover", aspectRatio: "1 / 1.15" }}
            />
          </div>
        </div>
      </section>

      {/* VSL — 90 seconds with Ernesto */}
      <section style={{ padding: "60px 40px 40px", maxWidth: 1120, margin: "0 auto", position: "relative", zIndex: 5 }} data-testid="ernesto-vsl-section">
        <VSLPlaceholder
          tag={lang === "es" ? "— MIRA · BIENVENIDA" : "— WATCH · WELCOME"}
          title={lang === "es"
            ? <>Cómo cierro deuda institucional <em style={{ fontStyle: "normal", color: GOLD }}>sin</em> diluir a los founders.</>
            : <>How I close institutional debt <em style={{ fontStyle: "normal", color: GOLD }}>without</em> diluting founders.</>}
          sub={lang === "es"
            ? "Presiona play — un mensaje rápido de bienvenida."
            : "Press play — a quick welcome message."}
          runtime="0:08"
          testid="ernesto-vsl"
        />
      </section>

      {/* Featured in press */}
      <FeaturedInStrip testid="ernesto-featured-in" />

      {/* PROOF */}
      <section style={{ padding: "40px 40px 80px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 5, borderTop: "1px solid rgba(15,56,44,0.08)" }} data-testid="ernesto-proof">
        <div className="tag" style={{ marginTop: 40 }}>{t.proof.tag}</div>
        <div style={{ marginTop: 32, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 32 }}>
          {t.proof.stats.map((s, i) => (
            <div key={i} data-testid={`ernesto-stat-${i}`}>
              <div className="serif" style={{ fontSize: 56, fontWeight: 400, color: GOLD, letterSpacing: "-0.03em", lineHeight: 1 }}>{s.n}</div>
              <div className="mono" style={{ fontSize: 10.5, letterSpacing: "0.2em", textTransform: "uppercase", opacity: 0.65, marginTop: 8 }}>{s.l}</div>
            </div>
          ))}
        </div>
        <div className="logo-strip" data-testid="ernesto-logos">
          {t.proof.logos.map((l) => (
            <span key={l}>{l}</span>
          ))}
        </div>

        <div className="tag" style={{ marginTop: 48 }}>{t.proof.fundsTag}</div>
        <div className="logo-strip" data-testid="ernesto-funds" style={{ marginTop: 20 }}>
          {t.proof.funds.map((l) => (<span key={l}>{l}</span>))}
        </div>

        <div className="tag" style={{ marginTop: 48 }}>{t.proof.credsTag}</div>
        <div style={{ marginTop: 20, display: "flex", flexWrap: "wrap", gap: 10 }} data-testid="ernesto-creds">
          {t.proof.creds.map((c) => (
            <span key={c} style={{ border: `1px solid ${GOLD}`, color: GOLD, borderRadius: 999, padding: "7px 14px", fontFamily: "'Archivo', sans-serif", fontSize: 11, fontWeight: 800, letterSpacing: "0.04em" }}>{c}</span>
          ))}
        </div>
      </section>

      {/* PRENSA & FORO FORBES */}
      <section style={{ padding: "40px 40px 90px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 5, borderTop: "1px solid rgba(15,56,44,0.08)" }} data-testid="ernesto-press">
        <div className="tag" style={{ marginTop: 40 }}>— PRENSA & FORO FORBES</div>
        <h2 className="section-title" style={{ marginTop: 20, maxWidth: 900 }}>
          En los <span className="serif" style={{ fontStyle: "normal", color: GOLD }}>foros de Forbes.</span>
        </h2>
        <div className="grid-3" style={{ marginTop: 44 }}>
          {VIDEOS.map((v, i) => (
            <a key={i} href={v.url} target="_blank" rel="noopener noreferrer" className="card" data-testid={`ernesto-video-${i}`} style={{ textDecoration: "none", color: "inherit", display: "block" }}>
              <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", border: "2px solid #141414", aspectRatio: "16 / 9", background: "#000" }}>
                <img src={v.thumb} alt={v.title} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.9 }} />
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: 54, height: 54, borderRadius: "50%", background: GOLD, border: "2px solid #141414", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#141414"><path d="M8 5v14l11-7z" /></svg>
                  </div>
                </div>
              </div>
              <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.24em", color: GOLD, textTransform: "uppercase", marginTop: 16 }}>{v.channel}</div>
              <div style={{ marginTop: 8, fontSize: 16, fontWeight: 800, lineHeight: 1.3 }}>{v.title}</div>
            </a>
          ))}
        </div>
      </section>

      {/* STORY */}
      <section style={{ padding: "120px 40px", background: BG_ALT, position: "relative", zIndex: 5 }} data-testid="ernesto-story">
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div className="tag">{t.story.tag}</div>
          <p className="serif" style={{ fontSize: "clamp(17px, 1.5vw, 22px)", opacity: 0.9, marginTop: 14, maxWidth: 780, fontStyle: "normal" }}>— {t.story.hook}</p>
          <h2 className="section-title" style={{ marginTop: 24 }}>
            {t.story.title1}<br />
            {t.story.title2}<br />
            <span className="serif" style={{ fontStyle: "normal", color: GOLD }}>{t.story.titleHL}</span>
          </h2>
          <div style={{ marginTop: 40, maxWidth: 780 }}>
            {t.story.body.map((p, i) => (
              <p key={i} style={{ marginTop: i === 0 ? 0 : 22, fontSize: 17, lineHeight: 1.7, opacity: 0.85 }} data-testid={`ernesto-story-p${i}`}>{p}</p>
            ))}
          </div>
          <div className="mono" style={{ marginTop: 36, fontSize: 11, letterSpacing: "0.22em", color: GOLD, textTransform: "uppercase" }}>Ernesto Gómez Berjón · {t.story.role}</div>
        </div>
      </section>

      {/* FOR WHO */}
      <section style={{ padding: "120px 40px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 5 }} data-testid="ernesto-who">
        <div className="tag">{t.forWho.tag}</div>
        <h2 className="section-title" style={{ marginTop: 22, maxWidth: 1000 }}>
          {t.forWho.title1} <span className="serif" style={{ fontStyle: "normal", color: GOLD }}>{t.forWho.titleHL}</span> {t.forWho.title2}
        </h2>
        <div className="grid-3" style={{ marginTop: 56 }}>
          {t.forWho.cards.map((c, i) => (
            <div key={i} className="card" data-testid={`ernesto-who-${i}`}>
              <div className="mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: GOLD }}>0{i + 1} · {c.k}</div>
              <p style={{ marginTop: 20, fontSize: 16, lineHeight: 1.6, opacity: 0.85 }}>{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCTS LADDER */}
      <section style={{ padding: "40px 40px 120px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 5 }} data-testid="ernesto-ladder">
        <div className="tag">{t.products.tag}</div>
        <p className="serif" style={{ fontSize: "clamp(17px, 1.5vw, 22px)", opacity: 0.9, marginTop: 14, maxWidth: 780, fontStyle: "normal" }}>— {t.products.hook}</p>
        <h2 className="section-title" style={{ marginTop: 24 }}>
          {t.products.title1}<br />
          <span className="serif" style={{ fontStyle: "normal", color: GOLD }}>{t.products.titleHL}</span>
        </h2>

        <div style={{ marginTop: 56, display: "flex", flexDirection: "column", gap: 12 }}>
          {t.products.ladder.map((r, i) => (
            <Link key={i} to={r.to} className="ladder-row" data-testid={`ernesto-ladder-${i}`} style={{ textDecoration: "none", color: "inherit" }}>
              <div className="ladder-tier">{r.tier}</div>
              <div>
                <div className="serif" style={{ fontSize: 22, fontWeight: 400, letterSpacing: "-0.02em" }}>{r.n}</div>
                <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.24em", opacity: 0.5, marginTop: 4, textTransform: "uppercase" }}>{r.flag}</div>
              </div>
              <p style={{ fontSize: 14.5, lineHeight: 1.55, opacity: 0.8 }}>{r.d}</p>
              <div className="serif" style={{ fontSize: 24, color: GOLD, letterSpacing: "-0.02em" }}>{r.price}</div>
              <div className="mono" style={{ fontSize: 10.5, letterSpacing: "0.22em", color: GOLD, textTransform: "uppercase" }}>{r.cta} →</div>
            </Link>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={{ padding: "120px 40px", background: BG_ALT, position: "relative", zIndex: 5 }} data-testid="ernesto-final">
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <div className="tag">{t.cta.tag}</div>
          <h2 className="section-title" style={{ marginTop: 22 }}>
            {t.cta.title1}<br />
            {t.cta.title2} <span className="serif" style={{ fontStyle: "normal", color: GOLD }}>{t.cta.titleHL}</span>
          </h2>
          <p className="serif" style={{ marginTop: 24, fontSize: "clamp(16px, 1.4vw, 20px)", opacity: 0.82, fontStyle: "normal" }}>{t.cta.desc}</p>
          <div style={{ display: "flex", gap: 14, marginTop: 40, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="#newsletter" className="btn-primary" data-testid="ernesto-final-cta">{t.cta.primary}</a>
          </div>
        </div>
      </section>

      <footer style={{ borderTop: "1px solid rgba(15,56,44,0.06)", padding: "48px 40px", maxWidth: 1400, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, flexWrap: "wrap", position: "relative", zIndex: 5 }} data-testid="ernesto-footer">
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <LogoMark size={26} />
          <span className="mono" style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", opacity: 0.5 }}>{t.footer}</span>
        </div>
        <Link to="/" className="mono" style={{ fontSize: 10.5, letterSpacing: "0.2em", textTransform: "uppercase", color: CREAM, opacity: 0.6, textDecoration: "none" }} data-testid="ernesto-footer-back">
          ← todos los tracks
        </Link>
      </footer>

      {/* Sticky CTA */}
      <StickyCTABar
        title={lang === "es" ? "Trabaja conmigo directamente" : "Work with me directly"}
        subtitle={lang === "es" ? "Diagnóstico de 20 min · cero venta agresiva" : "20-min diagnostic · no hard sell"}
        ctaText={lang === "es" ? "Ver programas" : "See programs"}
        ctaHref="#ladder"
        secondaryText={lang === "es" ? "Ebooks gratis →" : "Free ebooks →"}
        secondaryHref="/#biblioteca"
        testid="ernesto-sticky-cta"
      />
    </div>
  );
}
